import classNames from "classnames";
import React, { FC } from "react";
import { BsArchive } from "react-icons/bs";
import { HiMiniAdjustmentsHorizontal } from "react-icons/hi2";

import Button from "../../../components/button/Button";
import { InfoTooltip } from "../../../components/newUI/info-tooltip";
import PlusSvg from "../../../components/svg/PlusSvg";
import { Swiper } from "../../../components/swiper/Swiper";
import { ExcelExport } from "../../../utils/ExportData";

type BulkStatus = {
  color: string;
  name: string;
  statusId: string;
};

type Actions =
  | "import"
  | "export"
  | "change-status"
  | "archive"
  | "archive-icon"
  | "add-work"
  | "show-completed"
  | "filters-modal"
  | "delete";

type Props = {
  hasSelectedWorks: boolean;
  isStatusMenuOpen: boolean;
  setIsStatusMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  multipleStatuses: readonly BulkStatus[];
  onStatusChange: (status: string) => Promise<void> | void;
  onArchiveClick: () => void;
  onAddWorkClick?: () => void;
  /** When set, "Create Work" is disabled and this is its tip (archived client). */
  addWorkDisabledReason?: string | null;
  exportRows?: Record<string, unknown>[];
  exportFileName?: string;
  actionsClassName?: string;
  actions?: Actions[];

  isHideGreenStatus: boolean;
  setIsHideGreenStatus: React.Dispatch<React.SetStateAction<boolean>>;

  setIsOpenFilters?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const WorksBulkActions: FC<Props> = ({
  hasSelectedWorks,
  isStatusMenuOpen,
  setIsStatusMenuOpen,
  multipleStatuses,
  onStatusChange,
  onArchiveClick,
  onAddWorkClick,
  addWorkDisabledReason,
  exportRows,
  exportFileName,
  actionsClassName,
  actions = [],
  isHideGreenStatus,
  setIsHideGreenStatus,
  setIsOpenFilters,
}) => {
  return (
    <div className={classNames("flex items-center gap-3", actionsClassName)}>
      {actions.includes("show-completed") && (
        <div
          className={
            "relative left-0 top-0 mr-3 flex items-center gap-1 text-sm font-semibold"
          }>
          Show completed
          <Swiper
            value={!isHideGreenStatus}
            onChange={() => {
              setIsHideGreenStatus(!isHideGreenStatus);
            }}
            size={"sm"}
          />
          <InfoTooltip
            id={"show-completed-status-tooltip"}
            content={
              "If turned off, this will hide all the completed (green) statuses"
            }
            place={"bottom"}
            size={20}
            iconClassName={"text-gray-400 hover:text-blue-500"}
            tooltipClassName={"!max-w-[260px] !bg-gray-900"}
          />
        </div>
      )}

      {actions.includes("change-status") && (
        <div className={"relative"}>
          <div
            className={classNames(
              hasSelectedWorks ? "text-purple" : "text-gray-300",
              "w-[120px] cursor-pointer text-base font-semibold",
            )}
            onClick={() => {
              if (!hasSelectedWorks) return;
              setIsStatusMenuOpen((prev) => !prev);
            }}>
            Change status
          </div>

          {isStatusMenuOpen && (
            <div
              className={
                "absolute top-[30px] z-10 flex w-[150px] flex-col gap-1 rounded-[8px] border border-black border-opacity-[0.2] bg-white"
              }>
              {multipleStatuses.map((status) => (
                <div
                  key={status.statusId}
                  className={
                    "cursor-pointer px-3 py-2 text-sm hover:bg-gray-100"
                  }
                  onClick={() => void onStatusChange(status.name)}>
                  <div className={"flex items-center gap-2"}>
                    <div
                      className={"h-2 w-2 rounded-full"}
                      style={{ backgroundColor: status.color }}
                    />
                    <span>{status.name}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {actions.includes("archive") && (
        <button
          type={"button"}
          disabled={!hasSelectedWorks}
          className={classNames(
            "flex items-center gap-2 text-base font-semibold",
            hasSelectedWorks ? "text-purple" : "text-gray-300",
          )}
          onClick={() => {
            if (!hasSelectedWorks) return;
            onArchiveClick();
          }}>
          <BsArchive />
          Archive
        </button>
      )}

      {actions.includes("archive-icon") && (
        <button
          type={"button"}
          disabled={!hasSelectedWorks}
          onClick={() => {
            if (!hasSelectedWorks) return;
            onArchiveClick();
          }}
          className={"disabled:cursor-not-allowed disabled:opacity-40"}>
          <BsArchive
            data-tooltip-id={"tooltip-archive"}
            color={"#7c66f0"}
            size={"20px"}
            className={"flex items-center focus:outline-0 active:outline-0"}
          />
        </button>
      )}

      {exportRows && exportFileName && actions.includes("export") && (
        <ExcelExport data={exportRows} fileName={exportFileName} />
      )}

      {actions.includes("filters-modal") && setIsOpenFilters && (
        <Button
          label={""}
          icon={
            <HiMiniAdjustmentsHorizontal size={24} className={"text-purple"} />
          }
          onClick={() => setIsOpenFilters(true)}
          colorType={"ghost"}
        />
      )}

      {onAddWorkClick && actions.includes("add-work") && (
        <span title={addWorkDisabledReason || undefined}>
          <button
            onClick={onAddWorkClick}
            disabled={!!addWorkDisabledReason}
            className={
              "flex h-10 items-center justify-center gap-2 rounded-lg border border-[#6938EF] bg-[#6938EF] px-4 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
            }>
            <PlusSvg />
            <span>Create Work</span>
          </button>
        </span>
      )}
    </div>
  );
};
